import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  supabase,
  supabaseStorageKey,
  legacySupabaseStorageKeys,
  getInviteCallbackState,
  setInviteCallbackState,
  clearInviteCallbackState,
} from '../lib/supabaseClient';
import { fetchMe } from '../services/admin';
import { clearLoginNotice, storeLoginNotice } from '../lib/authRedirect';

const AuthContext = createContext({
  user: null,
  session: null,
  profile: null,
  loading: true,
  initialAuthLoading: true,
  profileLoading: false,
  profileRefreshing: false,
  profileError: null,
  authMessage: null,
  inviteCallbackState: null,
  clearAuthMessage: () => {},
});

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const PROFILE_REQUEST_TIMEOUT_MS = 10000;
const INITIAL_PROFILE_REQUEST_TIMEOUT_MS = 4000;

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [initialAuthLoading, setInitialAuthLoading] = useState(true);
  const [profileRefreshing, setProfileRefreshing] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [authMessage, setAuthMessage] = useState(null);
  const [inviteCallbackState, setInviteCallbackStateState] = useState(() => getInviteCallbackState());
  const profileRequestRef = useRef(null);
  const profileRef = useRef(null);
  const userRef = useRef(null);

  const applyProfile = useCallback((nextProfile) => {
    profileRef.current = nextProfile;
    setProfile(nextProfile);
  }, []);

  const applySessionUser = useCallback((nextSession) => {
    const nextUser = nextSession?.user ?? null;
    userRef.current = nextUser;
    setSession(nextSession);
    setUser(nextUser);
    return nextUser;
  }, []);

  const resetProfileState = useCallback(() => {
    profileRequestRef.current = null;
    applyProfile(null);
    setProfileError(null);
    setProfileRefreshing(false);
  }, [applyProfile]);

  const clearStoredSession = useCallback(() => {
    if (typeof window === 'undefined') return;
    const keysToClear = [supabaseStorageKey, ...legacySupabaseStorageKeys];

    keysToClear.forEach((key) => {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
    });

    Object.keys(window.localStorage)
      .filter((key) => /^sb-[^-]+-auth-token$/.test(key))
      .forEach((key) => window.localStorage.removeItem(key));

    Object.keys(window.sessionStorage)
      .filter((key) => /^sb-[^-]+-auth-token$/.test(key))
      .forEach((key) => window.sessionStorage.removeItem(key));
  }, []);

  const clearInviteFlowState = useCallback(() => {
    clearInviteCallbackState();
    setInviteCallbackStateState(null);
  }, []);

  const clearAuthMessage = useCallback(() => {
    setAuthMessage(null);
    clearLoginNotice();
  }, []);

  const completeSignOut = useCallback(async ({
    message = null,
    redirectToLogin = true,
  } = {}) => {
    try {
      if (supabase) {
        await Promise.race([
          supabase.auth.signOut(),
          new Promise((resolve) => setTimeout(resolve, 2000)),
        ]);
      }
    } finally {
      clearStoredSession();
      setUser(null);
      setSession(null);
      userRef.current = null;
      profileRequestRef.current = null;
      applyProfile(null);
      setProfileError(null);
      setProfileRefreshing(false);
      setInitialAuthLoading(false);
      clearInviteFlowState();

      if (message) {
        setAuthMessage(message);
        storeLoginNotice(message);
      } else {
        clearAuthMessage();
      }

      if (redirectToLogin) {
        navigate('/login', {
          replace: true,
          state: message ? { authMessage: message } : null,
        });
      }
    }
  }, [applyProfile, clearAuthMessage, clearInviteFlowState, clearStoredSession, navigate]);

  const normalizeProfileError = useCallback((err) => {
    const status = err?.response?.status ?? null;
    const detail = err?.response?.data?.detail;
    const code = typeof detail === 'object' ? detail?.code : null;
    const message = typeof detail === 'object'
      ? detail?.message
      : (err?.response?.data?.message || err?.message || 'Unable to verify workspace access.');

    if (status === 403 && code === 'ACCESS_NOT_PROVISIONED') {
      return {
        kind: 'unprovisioned',
        status,
        code,
        message: 'Your account has not been approved for pilot access yet.',
      };
    }

    if (status === 403 && code === 'ACCESS_DISABLED') {
      return {
        kind: 'disabled',
        status,
        code,
        message: 'Your pilot access is currently disabled.',
      };
    }

    if (status === 401) {
      return {
        kind: 'unauthorized',
        status,
        code: code || 'UNAUTHORIZED',
        message: 'Your session has ended. Please sign in again.',
      };
    }

    if (status === 502 || status === 503 || status === 504 || !err?.response) {
      return {
        kind: 'service_unavailable',
        status: status || 503,
        code: code || 'SERVICE_UNAVAILABLE',
        message: 'Workspace access is temporarily unavailable. Please try again in a moment.',
      };
    }

    return {
      kind: 'unknown',
      status: status || 500,
      code: code || 'PROFILE_CHECK_FAILED',
      message: message || 'Unable to verify workspace access.',
    };
  }, []);

  const fetchProfileWithTimeout = useCallback(async (timeoutMs = PROFILE_REQUEST_TIMEOUT_MS) => {
    return Promise.race([
      fetchMe(),
      wait(timeoutMs).then(() => {
        throw new Error('Workspace access check timed out.');
      }),
    ]);
  }, []);

  const refreshProfile = useCallback(({
    retries = 2,
    retryDelayMs = 700,
    preserveProfile = true,
    clearError = true,
    timeoutMs = PROFILE_REQUEST_TIMEOUT_MS,
  } = {}) => {
    const requestUserId = userRef.current?.id ?? null;

    if (profileRequestRef.current?.userId === requestUserId) {
      return profileRequestRef.current.promise;
    }

    const request = (async () => {
      const hadCachedProfile = Boolean(profileRef.current);

      setProfileRefreshing(true);
      if (clearError) {
        setProfileError(null);
      }

      try {
        for (let attempt = 0; attempt <= retries; attempt += 1) {
          try {
            const me = await fetchProfileWithTimeout(timeoutMs);
            if (requestUserId !== (userRef.current?.id ?? null)) {
              return null;
            }
            applyProfile(me);
            setProfileError(null);
            clearAuthMessage();
            return me;
          } catch (err) {
            const normalized = normalizeProfileError(err);
            const canRetry = normalized.kind === 'service_unavailable' && attempt < retries;

            if (canRetry) {
              await wait(retryDelayMs * (attempt + 1));
              continue;
            }

            if (requestUserId !== (userRef.current?.id ?? null)) {
              return null;
            }

            const shouldPreserveCachedProfile = preserveProfile && hadCachedProfile && normalized.kind !== 'unauthorized';

            if (!shouldPreserveCachedProfile) {
              applyProfile(null);
            }

            setProfileError(normalized);

            if (normalized.kind === 'unprovisioned' || normalized.kind === 'disabled' || normalized.kind === 'unauthorized') {
              await completeSignOut({
                message: normalized.message,
                redirectToLogin: true,
              });
            }

            return null;
          }
        }

        return null;
      } finally {
        if (profileRequestRef.current?.promise === request) {
          profileRequestRef.current = null;
        }
        setProfileRefreshing(false);
      }
    })();

    profileRequestRef.current = { userId: requestUserId, promise: request };
    return request;
  }, [applyProfile, clearAuthMessage, completeSignOut, fetchProfileWithTimeout, normalizeProfileError]);

  useEffect(() => {
    if (!supabase) { setInitialAuthLoading(false); return; }
    let mounted = true;

    const bootstrapAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        applySessionUser(data.session);

        if (data.session?.user) {
          await refreshProfile({
            retries: 0,
            timeoutMs: INITIAL_PROFILE_REQUEST_TIMEOUT_MS,
          });
        } else {
          resetProfileState();
        }
      } finally {
        if (mounted) {
          setInitialAuthLoading(false);
        }
      }
    };

    bootstrapAuth();

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (!mounted) return;
      const previousUserId = userRef.current?.id ?? null;
      const nextUser = applySessionUser(s);
      const nextUserId = nextUser?.id ?? null;
      const userChanged = Boolean(nextUserId) && previousUserId !== nextUserId;

      if (typeof window !== 'undefined') {
        const onInviteRoute = window.location.pathname === '/set-password'
          && new URLSearchParams(window.location.search).get('invite') === '1';
        if (onInviteRoute && s?.user && (event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY')) {
          setInviteCallbackState('accepted');
          setInviteCallbackStateState('accepted');
        } else if (event === 'SIGNED_OUT') {
          clearInviteFlowState();
        }
      }

      if (!s?.user) {
        clearStoredSession();
        resetProfileState();
        setInitialAuthLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        return;
      }

      if (userChanged) {
        resetProfileState();
        setInitialAuthLoading(true);
      }

      try {
        await refreshProfile({
          preserveProfile: !userChanged,
          retries: userChanged ? 0 : 2,
          timeoutMs: userChanged ? INITIAL_PROFILE_REQUEST_TIMEOUT_MS : PROFILE_REQUEST_TIMEOUT_MS,
        });
      } finally {
        if (userChanged && mounted) {
          setInitialAuthLoading(false);
        }
      }
    });
    return () => { mounted = false; sub?.subscription?.unsubscribe?.(); };
  }, [applySessionUser, clearInviteFlowState, clearStoredSession, refreshProfile, resetProfileState]);

  const probeSupabaseSignup = async (email, password) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: process.env.REACT_APP_SUPABASE_ANON_KEY },
        body: JSON.stringify({ email, password }),
      });
      const text = await res.text();
      let body = {};
      try { body = text ? JSON.parse(text) : {}; } catch { body = { msg: text }; }
      return { status: res.status, body };
    } catch { return null; }
  };

  const mapErr = (status, body) => {
    const code = body?.error_code || body?.code;
    const msg = body?.msg || body?.message || body?.error_description || '';
    if (status === 429 || /rate.*limit/i.test(code) || /rate.*limit/i.test(msg)) {
      return new Error('Supabase email rate limit reached. Wait an hour or disable email confirmation.');
    }
    if (/email_address_invalid/i.test(code) || /invalid.*email/i.test(msg)) return new Error('That email address is not accepted.');
    if (/user_already_exists/i.test(code) || /already registered/i.test(msg)) return new Error('An account with this email already exists. Try signing in instead.');
    if (/invalid.*credentials/i.test(msg) || /invalid.*login/i.test(msg)) return new Error('Invalid email or password.');
    return new Error(msg || `Auth error (HTTP ${status}).`);
  };

  const signUp = async (email, password) => {
    if (!supabase) throw new Error('Authentication not configured');
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) {
        const probe = await probeSupabaseSignup(email, password);
        if (probe) throw mapErr(probe.status, probe.body);
        throw new Error(error.message || 'Unable to sign up.');
      }
      return data;
    } catch (e) {
      if (/body stream already read/i.test(e?.message || '')) {
        const probe = await probeSupabaseSignup(email, password);
        if (probe) throw mapErr(probe.status, probe.body);
      }
      throw e instanceof Error ? e : new Error(String(e));
    }
  };

  const signIn = async (email, password) => {
    if (!supabase) throw new Error('Authentication not configured');
    clearAuthMessage();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw mapErr(error.status || 400, { msg: error.message, error_code: error.code });
    return data;
  };

  const signOut = async () => {
    await completeSignOut();
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading: initialAuthLoading,
      initialAuthLoading,
      profileLoading: profileRefreshing,
      profileRefreshing,
      profileError,
      authMessage,
      inviteCallbackState,
      signUp,
      signIn,
      signOut,
      refreshProfile,
      clearAuthMessage,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
